import type { Router } from 'vue-router'
import { startAuthorization } from '@/api/oauth'
import { useAppStore } from '@/stores/app'
import { useMenuStore } from '@/stores/menu'
import { usePermStore } from '@/stores/perm'
import { useUserStore } from '@/stores/user'
import { addMenuRoutes, flattenMenuPaths } from '@/utils/route'
import { wasLoggedOut } from '@/utils/pkce'
import { tokenState } from '@/utils/token'

/**
 * 路由守卫（附录C §2）：
 * 1. 无 access_token 时先静默续期（Cookie 里的 refresh 仍有效就当没断过），续不上才跳 /login；
 * 2. 进入管理后台前确保控制台系统的菜单与权限已加载；
 * 3. 越权兜底：不属于任何菜单分支的路径按 403 处理（服务端仍独立鉴权）。
 */

/**
 * 详情页（/admin/clients/{clientId}、/admin/users/7 …）带动态参数，天然不会出现在菜单树里，
 * 但它们是列表菜单的下钻路径，因此命中「菜单路径本身」或「其任意子路径」即放行。
 */
function isMenuPathAllowed(allowed: string[], target: string): boolean {
  return allowed.some((menuPath) => target === menuPath || target.startsWith(`${menuPath}/`))
}

/** 路径结构与菜单不一致的详情页用 meta.menuPath 声明归属菜单。 */
function resolveMenuPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '') {
    return null
  }
  return raw.startsWith('/') ? raw : `/admin/${raw}`
}

export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    if (to.meta.public) {
      return true
    }

    if (tokenState.accessToken.value === null) {
      // access 已镜像到 sessionStorage，同一标签页刷新不丢；跨标签页 / 过期再靠 uc_rt 续期
      const resumed = await useUserStore().bootstrap()
      if (!resumed) {
        // 用户刚刚主动退出过：不要再自动发起授权（否则会被 SSO 会话静默登录回去）
        if (wasLoggedOut()) {
          return { name: 'login', query: { redirect: to.fullPath } }
        }
        // 以「系统」身份发起授权码 + PKCE（附录C §2 / 附录D）：后台 uc-admin、个人中心 uc-me。
        await startAuthorization(to.fullPath, to.path.startsWith('/me') ? 'me' : 'admin')
        return false // 整页跳转已发起，本次路由导航取消
      }
    }

    // 个人中心不依赖系统上下文与菜单树（附录C §4：只调 /api/me/v1/*）
    if (to.path.startsWith('/me')) {
      return true
    }

    const appStore = useAppStore()
    // 刷新后令牌（含 systemUuid）是静默续期时才回来的；appStore 若在续期之前就被创建过，
    // consoleSystemUuid 会停在 null。这里补一次，否则下面的菜单/权限加载会被整段跳过。
    if (!appStore.consoleSystemUuid) {
      await appStore.loadSystems()
    }
    const consoleSystemUuid = appStore.consoleSystemUuid
    if (!consoleSystemUuid) {
      return true // 仍然拿不到：菜单与权限由 AdminLayout 加载后自行生效
    }

    const menuStore = useMenuStore()
    if (menuStore.loadedSystemUuid !== consoleSystemUuid) {
      // 菜单树是控制台自身的（stores/app.ts），切换"管理对象"不重拉
      await menuStore.load(consoleSystemUuid)
    }
    if (menuStore.tree.length > 0) {
      // 菜单只决定导航是否完整，不是鉴权边界：单条脏数据不该让整次导航失败
      try {
        addMenuRoutes(router, menuStore.tree)
      } catch (error) {
        console.error('菜单动态路由注册失败，按已注册路由继续', error)
      }
    }

    const permStore = usePermStore()
    if (permStore.loadedSystemUuid !== consoleSystemUuid) {
      await permStore.load(consoleSystemUuid)
    }

    if (to.path.startsWith('/admin') && to.path !== '/admin' && menuStore.tree.length > 0) {
      const allowed = flattenMenuPaths(menuStore.tree)
      // 详情页带动态参数，不在菜单树里：按「菜单路径 + 其子路径」放行；
      // 路径结构与菜单不一致的由 meta.menuPath 声明归属菜单
      const declared = resolveMenuPath(to.meta.menuPath)
      const targets = declared === null ? [to.path] : [to.path, declared]
      if (!targets.some((target) => isMenuPathAllowed(allowed, target))) {
        return { name: 'forbidden' }
      }
    }

    return true
  })
}
